// Simple email encryption/decryption utility
// Uses Base64 encoding with a simple ROT13 twist for obfuscation

export const encryptEmail = (email: string): string => {
  // First, apply ROT13 to letters only, preserving special characters
  const rot13 = email
    .split("")
    .map((char) => {
      if (char >= "a" && char <= "z") {
        return String.fromCharCode(((char.charCodeAt(0) - 97 + 13) % 26) + 97);
      }
      if (char >= "A" && char <= "Z") {
        return String.fromCharCode(((char.charCodeAt(0) - 65 + 13) % 26) + 65);
      }
      return char;
    })
    .join("");

  // Then encode to Base64 for additional obfuscation
  return btoa(rot13);
};

export const decryptEmail = (encrypted: string): string => {
  try {
    // Decode from Base64
    const decoded = atob(encrypted);

    // Apply ROT13 to letters only
    return decoded
      .split("")
      .map((char) => {
        if (char >= "a" && char <= "z") {
          return String.fromCharCode(
            ((char.charCodeAt(0) - 97 + 13) % 26) + 97,
          );
        }
        if (char >= "A" && char <= "Z") {
          return String.fromCharCode(
            ((char.charCodeAt(0) - 65 + 13) % 26) + 65,
          );
        }
        return char;
      })
      .join("");
  } catch (error) {
    console.error("Failed to decrypt email:", error);
    return "";
  }
};

// Alternative: Simple obfuscation without Base64
// This keeps the email partially visible but still protected
export const obfuscateEmail = (email: string): string => {
  return email
    .split("")
    .map((char) => {
      if (char >= "a" && char <= "z") {
        // Shift each letter by 1 (a->b, b->c, etc.)
        return String.fromCharCode(((char.charCodeAt(0) - 97 + 1) % 26) + 97);
      }
      if (char >= "A" && char <= "Z") {
        return String.fromCharCode(((char.charCodeAt(0) - 65 + 1) % 26) + 65);
      }
      if (char === "@") return "[at]";
      if (char === ".") return "[dot]";
      return char;
    })
    .join("");
};

export const deobfuscateEmail = (obfuscated: string): string => {
  return obfuscated
    .split("")
    .map((char) => {
      if (char >= "a" && char <= "z") {
        // Reverse the shift
        return String.fromCharCode(
          ((char.charCodeAt(0) - 97 - 1 + 26) % 26) + 97,
        );
      }
      if (char >= "A" && char <= "Z") {
        return String.fromCharCode(
          ((char.charCodeAt(0) - 65 - 1 + 26) % 26) + 65,
        );
      }
      return char;
    })
    .join("")
    .replace("[at]", "@")
    .replace("[dot]", ".");
};

// Pre-encrypted email for storage
export const ENCRYPTED_EMAIL = encryptEmail("contact@lensklicks.com");
export const OBFUSCATED_EMAIL = obfuscateEmail("contact@lensklicks.com");
